<template>
  <div
    :id="id"
    class="createbox"
    @contextmenu.prevent
  >
    <svg xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      :viewBox="dispViewBox"
      :style="'transform: ' + scale"
      preserveAspectRatio="xMinYMin meet"
      @mousemove="onMousemove"
      @mouseover="onMouseover"
      @mouseout="onMouseout"
      @wheel="onWheel"
    >
      <!-- グリッド※拡大するとぼやけるのでpatternでなくlineを直接描画 -->
      <g id="grid-lines" :stroke="gridLine.color" stroke-opacity="0.3">
        <g
          v-for="grid in gridList"
          :key="'grid-' + grid.pitch"
          :stroke-width="grid.scaleStrokeWidth"
          :stroke-dasharray="gridLine.dasharray"
        >
          <!-- x方向 -->
          <line
            v-for="x in grid.pitchXList"
            :key="grid.pitch + '-x-' + x"
            :x1="x" :y1="viewBox.minY"
            :x2="x" :y2="viewBox.minY + viewBox.height"
          />
          <!-- y方向 -->
          <line
            v-for="y in grid.pitchYList"
            :key="grid.pitch + '-y-' + y"
            :y1="y" :x1="viewBox.minX"
            :y2="y" :x2="viewBox.minX + viewBox.width"
          />
        </g>
      </g>

      <!-- 空間データ -->
      <!--
        【パフォーマンス改善】
        ・表示対象の空間データ(dispGlobalSpaces)を極力絞ってからdd-spaceコンポーネントを生成すること
        ・dd-spaceコンポーネントに渡すpropsについて、
          ・対象の空間に該当する情報だけに絞って渡すこと
          ・値が変わっていないものについてはコンポーネント内の内部処理が走らないようにすること
      -->
      <g id="space-lines">
        <dd-space
          v-for="space in dispGlobalSpaces"
          :key="'space-' + space.id"
          :space="space"
          :addTargetSpace="addTargetSpace"
          :editTargetCellSpaceParentId="editTargetCellSpaceParent ? editTargetCellSpaceParent.id : undefined"
          :viewSet="viewSet"
          :isDispProcess="isDispProcess"
          :scaleStrokeWidth="scaleStrokeWidth"
          :showInfillLine="showInfillLine"
          :doorOpenLineMap="doorOpenLineMap[space.id]"
          :convertedSectionPosition="convertedSectionPosition"
          :hiddenSpaces="hiddenSpacesMap[space.id]"
          :getDashArray="getDashArray"
          :fileData="fileData"
          :objectDataModel="objectDataModelMap[space.id]"
          :isDispAccessory="isDispAccessory"
          :focusedProcessId="focusedProcessId"
        />
      </g>

      <!-- 軸線 -->
      <g id="axis-lines"
        :stroke="axisLine.color"
        :stroke-width="scaleStrokeWidth(axisLine.width)"
        :stroke-dasharray="axisLine.dasharray">
        <line :x1="minValue" y1="0" :x2="maxValue" y2="0" />
        <line x1="0" :y1="minValue" x2="0" :y2="maxValue" />
      </g>

      <!-- 操作用空間 -->
      <g id="ctrl-spaces" fill="none">
        <!-- 編集 -->
        <rect
          v-for="dispSpace in dispEditCtrlSpaces"
          :key="'ctrl-' + dispSpace.id"
          :x="dispSpace.x"
          :y="dispSpace.y"
          :width="dispSpace.w"
          :height="dispSpace.h"
          :stroke-width="scaleStrokeWidth(editSpacesLine.width)"
          :stroke-dasharray="editSpacesLine.dasharray"
          :stroke="editSpacesLine.color"
        />
        <!-- グループ -->
        <rect
          v-if="groupCtrlSpace && Object.keys(groupCtrlSpace).length"
          :x="dispGroupCtrlSpace.x"
          :y="dispGroupCtrlSpace.y"
          :width="dispGroupCtrlSpace.w"
          :height="dispGroupCtrlSpace.h"
          :stroke-width="scaleStrokeWidth(groupCtrlSpacesLine.width)"
          :stroke-dasharray="groupCtrlSpacesLine.dasharray"
          :stroke="groupCtrlSpacesLine.color"
        />
        <!-- 追加予定位置 -->
        <rect
          v-if="Object.keys(dispLocateAddSpaceLines).length && !Object.keys(dispTargetSwapSpaceLines).length"
          :x="dispLocateAddSpaceLines.x"
          :y="dispLocateAddSpaceLines.y"
          :width="dispLocateAddSpaceLines.w"
          :height="dispLocateAddSpaceLines.h"
          :stroke-width="scaleStrokeWidth(locateAddSpaceLine.width)"
          :stroke-dasharray="locateAddSpaceLine.dasharray"
          :stroke="locateAddSpaceLine.color"
        />
        <!-- 上書き対象空間 -->
        <rect
          v-if="Object.keys(dispTargetSwapSpaceLines).length"
          :x="dispTargetSwapSpaceLines.x"
          :y="dispTargetSwapSpaceLines.y"
          :width="dispTargetSwapSpaceLines.w"
          :height="dispTargetSwapSpaceLines.h"
          :stroke-width="scaleStrokeWidth(targetSwapSpaceLine.width)"
          :stroke-dasharray="targetSwapSpaceLine.dasharray"
          :stroke="targetSwapSpaceLine.color"
        />
        <!-- 表面オプションの配置対象の空間 -->
        <rect
          v-if="Object.keys(dispTargetFaceOpAddSpaceLines).length"
          :x="dispTargetFaceOpAddSpaceLines.x"
          :y="dispTargetFaceOpAddSpaceLines.y"
          :width="dispTargetFaceOpAddSpaceLines.w"
          :height="dispTargetFaceOpAddSpaceLines.h"
          :stroke-width="scaleStrokeWidth(targetFaceOpUnitAddSpaceLine.width)"
          :stroke-dasharray="targetFaceOpUnitAddSpaceLine.dasharray"
          :stroke="targetFaceOpUnitAddSpaceLine.color"
        />
        <!-- 追加 -->
        <rect
          v-if="addCtrlSpace && Object.keys(addCtrlSpace).length"
          :x="dispAddCtrlSpace.x"
          :y="dispAddCtrlSpace.y"
          :width="dispAddCtrlSpace.w"
          :height="dispAddCtrlSpace.h"
          :stroke-width="scaleStrokeWidth(addCtrlSpaceLine.width)"
          :stroke-dasharray="addCtrlSpaceLine.dasharray"
          :stroke="addCtrlSpaceLine.color"
        />
      </g>

      <!-- 強調線 -->
      <g id="focus-space">
        <rect
          v-if="dispFocusedSpace && Object.keys(dispFocusedSpace).length"
          :x="dispFocusedSpace.x"
          :y="dispFocusedSpace.y"
          :width="dispFocusedSpace.w"
          :height="dispFocusedSpace.h"
          :stroke-width="scaleStrokeWidth(focusSpaceLine.width)"
          :stroke-dasharray="focusSpaceLine.dasharray"
          :stroke="focusSpaceLine.color"
          fill="none"
        />
      </g>

      <!-- 吸着線 -->
      <g id="snap-lines" v-if="viewSet.viewType === currentView && !Object.keys(dispTargetSwapSpaceLines).length">
        <line
          v-for="(snapLine, index) in snapLines"
          :key="'snap-' + index"
          :x1="snapLine.x1"
          :y1="snapLine.y1"
          :x2="snapLine.x2"
          :y2="snapLine.y2"
          :stroke-width="scaleStrokeWidth(dispSnapLine.width)"
          :stroke-dasharray="dispSnapLine.dasharray"
          :stroke="dispSnapLine.color"
        />
      </g>

      <!-- 断面線 -->
      <g id="section-lines">
        <line
          v-for="(sectionLine, index) in dispSectionPositionLines"
          :key="'sec-' + index"
          :x1="sectionLine.value.x1"
          :y1="sectionLine.value.y1"
          :x2="sectionLine.value.x2"
          :y2="sectionLine.value.y2"
          :stroke-width="dispSectionPositionStrokeWidth(sectionLine.axis)"
          :stroke-dasharray="dispSectionLine.dasharray"
          :stroke="dispSectionLine.color"
        />
        <symbol id="side-icon">
          <path
          :d="dispSideIcon()"
          :fill="dispSectionLine.color"></path>
        </symbol>
        <symbol id="bottom-icon">
          <path
          :d="dispBottomIcon()"
          :fill="dispSectionLine.color"></path>
        </symbol>
        <use
          xlink:href="#side-icon"
          :x = "- scaleStrokeWidth(40)"
          :y = "dispSectionPositionLines[1].value.y1 - scaleStrokeWidth(25)" />
        <use
          xlink:href="#bottom-icon"
          :x = "dispSectionPositionLines[0].value.x1 - scaleStrokeWidth(25)"
          :y = "- scaleStrokeWidth(40)" />
      </g>
      <!-- 図面断面線 -->
      <g id="section-point-lines">
        <g
          v-for="(sectionLine, index) in dispSectionPointLines"
          :key="'sheetSec-' + index">
          <line
            :x1="sectionLine.value.x1"
            :y1="sectionLine.value.y1"
            :x2="sectionLine.value.x2"
            :y2="sectionLine.value.y2"
            :stroke-width="dispSectionPointStrokeWidth(sectionLine.lineNo)"
            :stroke-dasharray="sectionLine.dasharray"
            :stroke="sectionPointLine.color"
          />
          <symbol id="side-point-icon">
            <path
              :d="dispSideIcon()"
              :fill="sectionPointLine.color"></path>
          </symbol>
          <symbol id="bottom-point-icon">
            <path
            :d="dispBottomIcon()"
            :fill="sectionPointLine.color"></path>
          </symbol>
          <use
            v-if="sectionLine.axis === 'Y'"
            xlink:href="#side-point-icon"
            :x = "- scaleStrokeWidth(40)"
            :y = "sectionLine.value.y1 - scaleStrokeWidth(25)" />
          <use
            v-if="sectionLine.axis === 'X'"
            xlink:href="#bottom-point-icon"
            :x = "sectionLine.value.x1 - scaleStrokeWidth(25)"
            :y = "- scaleStrokeWidth(40)" />
          <text
            :x="viewSet.inverse.x ? -sectionLine.value.x1 - fontSize / 0.8 : sectionLine.value.x1"
            :y="viewSet.inverse.y ? sectionLine.value.y1 + fontSize : -sectionLine.value.y1"
            fill="ff1010"
            :transform="scale"
            :font-size="fontSize"
          >
            {{sectionLine.label}}
          </text>
        </g>
      </g>

      <!-- 面選択線 -->
      <g id="face-lines">
        <!-- 選択対象 -->
        <line
          v-if="Object.keys(dispSelectTargetFaceLine).length"
          :x1="dispSelectTargetFaceLine.x1"
          :y1="dispSelectTargetFaceLine.y1"
          :x2="dispSelectTargetFaceLine.x2"
          :y2="dispSelectTargetFaceLine.y2"
          :stroke-width="scaleStrokeWidth(faceBeforeLine.width)"
          :stroke-dasharray="faceBeforeLine.dasharray"
          :stroke="faceBeforeLine.color"
        />
        <!-- 選択面 -->
        <line
          v-for="(selectedFace, idx) in dispSelectedFaceLine"
          :key="'face-' + idx"
          :x1="selectedFace.x1"
          :y1="selectedFace.y1"
          :x2="selectedFace.x2"
          :y2="selectedFace.y2"
          :stroke-width="scaleStrokeWidth(faceAfterLine.width)"
          :stroke-dasharray="faceAfterLine.dasharray"
          :stroke="faceAfterLine.color"
        />
      </g>

      <!-- 寸法値 -->
      <g id="dim-texts">
        <!-- Y軸 -->
        <g
          v-for="y in pitchList(dimPitch, viewBox.minY, viewBox.minY + viewBox.height)"
          :key="'y-' + y"
          :text-anchor="viewSet.inverse.x ? 'end' : 'start'"
          dominant-baseline="middle"
          :font-size="fontSize"
          :transform="scale"
        >
          <!-- マスク用 -->
          <text
            class="text-mask"
            v-if="svgZoomRatio >= 0.5"
            :x="viewSet.inverse.x ? -viewBox.minX : viewBox.minX"
            :y="viewSet.inverse.y ? y : -y"
          >
            {{ y }}
          </text>
          <text
            :x="viewSet.inverse.x ? -viewBox.minX : viewBox.minX"
            :y="viewSet.inverse.y ? y : -y"
          >
            {{ y }}
          </text>
        </g>
        <!-- X軸 -->
        <g
          v-for="x in pitchList(dimPitch, viewBox.minX, viewBox.minX + viewBox.width)"
          :key="'x-' + x"
          :font-size="fontSize"
          text-anchor="middle"
          :dominant-baseline="viewSet.inverse.y ? 'text-before-edge' : 'text-after-edge'"
          :transform="scale"
        >
          <!-- マスク用 -->
          <text
            class="text-mask"
            v-if="svgZoomRatio >= 0.5"
            :x="viewSet.inverse.x ? -x : x"
            :y="viewSet.inverse.y ? viewBox.minY : -viewBox.minY"
          >
            {{ x }}
          </text>
          <text
            :x="viewSet.inverse.x ? -x : x"
            :y="viewSet.inverse.y ? viewBox.minY : -viewBox.minY"
          >
            {{ x }}
          </text>
        </g>
      </g>

      <!-- 選択範囲 -->
      <g id="select-area">
        <rect
          v-if="Object.keys(selectArea).length"
          :x="dispSelectArea.x"
          :y="dispSelectArea.y"
          :width="dispSelectArea.w"
          :height="dispSelectArea.h"
          :stroke-width="scaleStrokeWidth(selectAreaLine.width)"
          :stroke-dasharray="selectAreaLine.dasharray"
          :stroke="selectAreaLine.color"
          :fill="selectAreaLine.color"
          fill-opacity="0.1"
        />
      </g>

      <!-- 位置ガイド線 -->
      <g id="position-guide-lines" v-if="!Object.keys(dispTargetSwapSpaceLines).length">
        <defs>
          <marker id="arrow" markerUnits="userSpaceOnUse" markerWidth="30" markerHeight="30" viewBox="0 0 10 10" refX="5" refY="5" orient="auto-start-reverse">
            <polygon points="0,0 5,5 0,10 10,5 " :fill="positionGuideLine.color"/>
          </marker>
          <marker id="arrow_reverse" markerUnits="userSpaceOnUse" markerWidth="30" markerHeight="30" viewBox="0 0 10 10" refX="5" refY="5" orient="auto-start-reverse">
            <polygon points="10,0 5,5 10,10 0,5 " :fill="positionGuideLine.color"/>
          </marker>
        </defs>
        <g
          v-for="(guideLine, index) in condPositionGuideLines"
          :key="'guide-' + index"
          :stroke="positionGuideLine.color"
        >
          <line
            :x1="guideLine.x1 - markerEndOffset(guideLine.x1, guideLine.x2)"
            :y1="guideLine.y1 - markerEndOffset(guideLine.y1, guideLine.y2)"
            :x2="guideLine.x2 + markerEndOffset(guideLine.x1, guideLine.x2)"
            :y2="guideLine.y2 + markerEndOffset(guideLine.y1, guideLine.y2)"
            :stroke-width="guideLineLength(guideLine) >= 30 ? scaleStrokeWidth(positionGuideLine.width) : scaleStrokeWidth(1)"
            :stroke-dasharray="positionGuideLine.dasharray"
            :marker-start="guideLineLength(guideLine) >= 30 ? 'url(#arrow)' : 'url(#arrow_reverse)'"
            :marker-end="guideLineLength(guideLine) >= 30 ? 'url(#arrow)' : 'url(#arrow_reverse)'"
          />
          <text
            :x="(viewSet.inverse.x ? -1 : 1) * ((guideLine.x1 + guideLine.x2) / 2 + (guideLine.y1 !== guideLine.y2 ? 10 : 0))"
            :y="(viewSet.inverse.y ? 1 : -1) * ((guideLine.y1 + guideLine.y2) / 2 + (guideLine.x1 !== guideLine.x2 ? 5 : 0))"
            :font-size="fontSize"
            :text-anchor="guideLine.x1 !== guideLine.x2 ? 'middle' : (viewSet.inverse.x ? 'end' : 'start')"
            :dominant-baseline="viewSet.inverse.y ? 'text-before-edge' : 'text-after-edge'"
            :transform="scale"
          >
            {{ guideLineLength(guideLine) }}
          </text>
          <!-- 端線 -->
          <g class="aaa" v-if="guideLine.endMark" :stroke-width="scaleStrokeWidth(positionGuideLine.width)" stroke-dasharray="5 5">
            <g v-if="guideLine.x1 !== guideLine.x2">
              <line :x1="guideLine.x1" :y1="guideLine.y1 - 50" :x2="guideLine.x1" :y2="guideLine.y1"/>
              <line :x1="guideLine.x2" :y1="guideLine.y2 - 50" :x2="guideLine.x2" :y2="guideLine.y2"/>
            </g>
            <g v-if="guideLine.y1 !== guideLine.y2">
              <line :x1="guideLine.x1 - 50" :y1="guideLine.y1" :x2="guideLine.x1" :y2="guideLine.y1"/>
              <line :x1="guideLine.x2 - 50" :y1="guideLine.y2" :x2="guideLine.x2" :y2="guideLine.y2"/>
            </g>
          </g>
        </g>
      </g>

    </svg>

    <!-- TODO 一旦使わないためコメントアウト -->
    <!-- ビュー選択 -->
    <!--<div v-if="showViewTypeSelect" class="selectbox">-->
      <!--<select-->
        <!--:class="'viewpoint create_' + canvasType"-->
        <!--v-model="selectedViewType"-->
        <!--@change="onViewTypeChanged"-->
        <!--@mousedown.stop-->
      <!--&gt;-->
        <!--<option v-for="option in viewTypes" :value="option" :key="option.code">-->
          <!--{{ option.label }}-->
        <!--</option>-->
      <!--</select>-->
    <!--</div>-->
    <!--<div v-else :class="'viewpoint create_' + canvasType">-->
      <!--<p @mousedown.stop>{{ viewSet.viewType.label }}</p>-->
    <!--</div>-->

  </div>
</template>

<script>
import {VIEW_SET, AXIS_TYPE, OBJECT_TYPE, VIEW_TYPE, PLANNING_TYPE, FACE_TYPE, ATTRIBUTE_KEY, OBJECT_DTL_TYPE} from '../../../constants/Type'
import {SVG_MAX_VALUE, SVG_FONT_SIZE, SVG_DIM_PITCH, SVG_GRID, SVG_LINE} from '../../../constants/App'
import Space from './Space'
import * as SpaceUtil from '../../../utils/Space'
import * as FaceUtil from '../../../utils/Face'
import * as CalcUtil from '../../../utils/Calc'

export default {
  name: 'Canvas',
  components: {
    'dd-space': Space
  },
  props: {
    // *****************************************************************
    // キャンバス表示制御
    // *****************************************************************
    /** キャンバスdiv要素ID */
    id: String,
    /** SVG viewBox */
    viewBox: Object,
    /** ビュー選択セレクトボックス表示フラグ */
    showViewTypeSelect: Boolean,
    /** キャンバスタイプ */
    canvasType: String, // Type.CANVAS_TYPE
    /** ビューの種別情報の定義情報 */
    viewSet: Object, // Type.VIEW_SET
    /** プランニング画面で操作中のビュー */
    currentView: Object, // Type.VIEW_TYPE
    /** 吸着線 */
    snapLines: Array,
    /** 位置ガイド線 */
    positionGuideLines: Array,
    /** 断面線表示位置 */
    sectionPosition: Object,
    /** 操作中の断面線 */
    activeSectionPosition: String,
    /** 図面断面位置リスト */
    sectionPoints: Array,
    /** 走査中の断面位置 */
    activeSectionPoint: Number,
    /** 選択範囲 */
    selectArea: Object,
    /** 扉開閉線 */
    doorOpenLineMap: Object,
    /* スケルトンと配下の空間IDのマップ */
    sklUnderSpaceIdsMap: {},
    /** ズーム率 */
    svgZoomRatio: Number,
    /** 加工線表示 */
    isDispProcess: Boolean,
    /** アクセサリー表示 */
    isDispAccessory: Boolean,
    /** ダミー空間表示 */
    isDispDummy: Boolean,
    // *****************************************************************
    // 空間データ
    // *****************************************************************
    /** ファイルデータ */
    fileData: Object,
    /** ビューの向きに応じた座標系およびグローバル座標に変換した空間データ */
    globalSpaces: Array,
    /** 表示対象空間オブジェクト種別 */
    dispSpaceObjectTypes: Array,
    /** グループ操作用空間データ */
    groupCtrlSpace: Object,
    /** グローバル座標空間マップ */
    globalSpaceMap: Map,
    /** 編集操作用空間データリスト */
    editCtrlSpaces: Array,
    /** 追加操作用空間データ */
    addCtrlSpace: Object,
    /** 追加対象空間データ */
    addTargetSpace: Object,
    /** 編集対象親セル空間データ */
    editTargetCellSpaceParent: Object,
    /** 選択対象面{space: SpaceModel, faceType: FACE_TYPE} */
    selectTargetFace: Object,
    /** 選択面[{space: SpaceModel, faceType: FACE_TYPE},...] */
    selectedFaces: Array,
    /** 強調表示空間ID */
    focusedSpaceId: null,
    /** 強調表示加工ID */
    focusedProcessId: Number,
    /** オブジェクトデータモデルのオブジェクト */
    objectDataModelMap: Object
  },
  watch: {
    viewSet: function () {
      this.selectedViewType = this.viewSet.viewType
    }
  },
  data () {
    return {
      /** セレクトボックスで選択されたビュー種別 */
      selectedViewType: this.viewSet.viewType
    }
  },
  computed: {
    // *****************************************************************
    // 表示制御(キャンバス)
    // *****************************************************************
    /** メインビューでのセレクトボックスで選択可能なビュー種別一覧 */
    viewTypes: function () {
      return VIEW_SET.map(viewSet => viewSet.main.viewType)
    },
    dispViewBox: function () {
      return this.viewBox.minX + ' ' + this.viewBox.minY + ' ' + this.viewBox.width + ' ' + this.viewBox.height
    },
    /** 選択範囲表示 */
    dispSelectArea: function () {
      if (!this.selectArea || !Object.keys(this.selectArea).length) return {}
      if (this.viewSet.viewType !== this.currentView) return {}
      let x = this.selectArea.x1 < this.selectArea.x2 ? this.selectArea.x1 : this.selectArea.x2
      let y = this.selectArea.y1 < this.selectArea.y2 ? this.selectArea.y1 : this.selectArea.y2
      let w = Math.abs(this.selectArea.x2 - this.selectArea.x1)
      let h = Math.abs(this.selectArea.y2 - this.selectArea.y1)
      return { x, y, w, h }
    },
    /** SVGで扱う最大値 */
    maxValue: function () {
      return SVG_MAX_VALUE
    },
    /** SVGで扱う最小値 */
    minValue: function () {
      return -1 * SVG_MAX_VALUE
    },
    fontSize: function () {
      return SVG_FONT_SIZE * this.svgZoomRatio
    },
    /** ビューの反転制御 */
    scale: function () {
      return 'scale(' + (this.viewSet.inverse.x ? -1 : 1) + ', ' + (this.viewSet.inverse.y ? 1 : -1) + ')'
    },
    /** グリッド線 */
    gridList: function () {
      const { svgZoomRatio, scaleStrokeWidth, pitchList } = this
      const { minX, minY, width, height } = this.viewBox
      return SVG_GRID.filter(e => svgZoomRatio <= e.threshold).map(e => ({
        ...e,
        scaleStrokeWidth: scaleStrokeWidth(e.strokeWidth),
        pitchXList: pitchList(e.pitch, minX, minX + width),
        pitchYList: pitchList(e.pitch, minY, minY + height)
      }))
    },
    /** 寸法値の間隔取得 */
    dimPitch: function () {
      return SVG_DIM_PITCH.find(e => e.threshold < this.svgZoomRatio).pitch
    },
    // *****************************************************************
    // 表示データ
    // *****************************************************************
    /** グループ操作用空間の表示データ */
    dispGroupCtrlSpace: function () {
      return this.getDispCtrlSpace(this.groupCtrlSpace)
    },
    /** 追加操作用空間の表示データ */
    dispAddCtrlSpace: function () {
      if (Object.keys(this.getDispLocateAddSpaceLines()).length || Object.keys(this.getDispTargetSwapSpaceLines()).length || Object.keys(this.getDispTargetFaceOpAddSpaceLines()).length) {
        return {}
      }
      return this.getDispCtrlSpace(this.addCtrlSpace, false)
    },
    /** 編集操作用空間の表示データ */
    dispEditCtrlSpaces: function () {
      return this.editCtrlSpaces.map(space => this.getDispCtrlSpace(space)).filter(space => Object.keys(space).length)
    },
    /** 断面線の表示データ */
    dispSectionPositionLines: function () {
      let converted = this.convertedSectionPosition
      return [
        {axis: AXIS_TYPE.X, value: {x1: converted.x, y1: 0, x2: converted.x, y2: this.maxValue}},
        {axis: AXIS_TYPE.Y, value: {x1: 0, y1: converted.y, x2: this.maxValue, y2: converted.y}}
      ]
    },
    /** ビューの向きに応じた断面線位置 */
    convertedSectionPosition: function () {
      return SpaceUtil.convertSpaceDataForDisp(this.sectionPosition, this.viewSet.viewType, this.viewSet.rotate)
    },
    dispSectionPointLines: function () {
      return this.convertedSectionPoints.filter(sec => {
        switch (this.viewSet.viewType) {
          case VIEW_TYPE.FRONT:
          case VIEW_TYPE.BACK:
            return sec.axis !== 'Z'
          case VIEW_TYPE.BOTTOM:
          case VIEW_TYPE.TOP:
            return sec.axis !== 'Y'
          case VIEW_TYPE.LEFT:
          case VIEW_TYPE.RIGHT:
            return sec.axis !== 'X'
        }
      }).map(converted => {
        let xAxis
        switch (this.viewSet.viewType) {
          case VIEW_TYPE.FRONT:
          case VIEW_TYPE.BACK:
          case VIEW_TYPE.BOTTOM:
          case VIEW_TYPE.TOP:
            xAxis = converted.axis === 'X'
            break
          case VIEW_TYPE.LEFT:
          case VIEW_TYPE.RIGHT:
            xAxis = converted.axis === 'Z'
            break
        }
        return {
          axis: xAxis ? AXIS_TYPE.X : AXIS_TYPE.Y,
          value: {
            x1: xAxis ? converted.x : 0,
            y1: xAxis ? 0 : converted.y,
            x2: xAxis ? converted.x : this.maxValue,
            y2: xAxis ? this.maxValue : converted.y
          },
          lineNo: converted.lineNo,
          label: converted.label,
          delFlg: converted.delFlg
        }
      })
    },
    convertedSectionPoints: function () {
      let axis = FaceUtil.getVerticalAxis(this.viewSet.viewType.code)
      return this.sectionPoints.filter(sec => !sec.delFlg).filter(sec => sec.axis !== axis).map(sec => {
        let point = {
          x: sec.axis === 'X' ? sec.pos : 0,
          y: sec.axis === 'Y' ? sec.pos : 0,
          z: sec.axis === 'Z' ? sec.pos : 0
        }
        let converted = SpaceUtil.convertSpaceDataForDisp(point, this.viewSet.viewType, this.viewSet.rotate)
        converted.axis = sec.axis
        converted.lineNo = sec.lineNo
        converted.label = sec.label
        converted.delFlg = sec.delFlg
        return converted
      })
    },
    /** 面選択対象線の表示データ */
    dispSelectTargetFaceLine: function () {
      if (this.viewSet.viewType !== this.currentView ||
        !this.dispGlobalSpaces || !Object.keys(this.dispGlobalSpaces).length ||
        !this.selectTargetFace || !Object.keys(this.selectTargetFace).length ||
        !this.selectTargetFace.space || !Object.keys(this.selectTargetFace.space).length ||
        !this.selectTargetFace.faceType || this.selectTargetFace.faceType === FACE_TYPE.UNDEFINED ||
        !this.dispGlobalSpaces.find(space => space.id === this.selectTargetFace.space.id)) {
        return {}
      }
      return FaceUtil.getFaceLine(
        this.selectTargetFace.space,
        this.selectTargetFace.faceType,
        this.viewSet.viewType,
        this.viewSet.inverse,
        this.viewSet.rotate
      )
    },
    /** 面選択済み線の表示データ */
    dispSelectedFaceLine: function () {
      if (!this.dispGlobalSpaces || !Object.keys(this.dispGlobalSpaces).length ||
        !this.selectedFaces || !this.selectedFaces.length) {
        return {}
      }
      return this.selectedFaces.map(selectedFace => {
        return FaceUtil.getFaceLine(
          selectedFace.space,
          selectedFace.faceType,
          this.viewSet.viewType,
          this.viewSet.inverse,
          this.viewSet.rotate
        )
      })
    },
    /** 空間の追加予定位置の表示データ */
    dispLocateAddSpaceLines: function () {
      return this.getDispLocateAddSpaceLines()
    },
    /** 上書き対象の空間位置の表示データ */
    dispTargetSwapSpaceLines: function () {
      return this.getDispTargetSwapSpaceLines()
    },
    /** 表面オプションの配置対象空間位置の表示データ */
    dispTargetFaceOpAddSpaceLines: function () {
      return this.getDispTargetFaceOpAddSpaceLines()
    },
    /** 空間の表示データ */
    dispGlobalSpaces: function () {
      if (!this.globalSpaces || !Object.keys(this.globalSpaces).length) return []
      const { z: convertedSectionPositionZ } = this.convertedSectionPosition
      const { viewType, rotate } = this.viewSet
      return this.globalSpaces.filter(({ id: spaceId, d: spaceD, z: spaceZ, spaceObjectType, spaceObjectDtlType, parentSpaceId, planningType }) => {
        if (spaceObjectType === OBJECT_TYPE.ROOM.code) return false
        // **********************************************************************
        // 断面位置による制御
        // **********************************************************************
        if (viewType.isBase) {
          if (spaceZ + spaceD < convertedSectionPositionZ) {
            this.setOutOfSectionSpaceId(true, viewType, spaceId)
            return false
          } else {
            this.setOutOfSectionSpaceId(false, viewType, spaceId)
          }
        } else {
          if (convertedSectionPositionZ < spaceZ) {
            this.setOutOfSectionSpaceId(true, viewType, spaceId)
            return false
          } else {
            this.setOutOfSectionSpaceId(false, viewType, spaceId)
          }
        }

        // **********************************************************************
        // 強調線は常にOK
        // **********************************************************************
        if (spaceId === this.focusedSpaceId) return true

        // **********************************************************************
        // 種別による制御
        // **********************************************************************
        // 処理速度改善のため、算出プロパティで生成するマップから取得
        const objectDataModel = this.objectDataModelMap[spaceId]
        const isDummy = [OBJECT_DTL_TYPE.UNIT_CONST_.code, OBJECT_DTL_TYPE.UNIT_INNER_SEPARATOR_DAMMY.code,
          OBJECT_DTL_TYPE.UNIT_SKELTON_DAMMY.code, OBJECT_DTL_TYPE.PARTS_DAMMY.code].includes(spaceObjectDtlType)
        if (this.isDispDummy && isDummy) {
          // 表示対象ダミー空間の場合
          return true
        } else if (!isDummy && spaceObjectType && this.dispSpaceObjectTypes &&
          this.dispSpaceObjectTypes.includes(spaceObjectType)) {
          // 表示対象オブジェクト種別の場合
          return true
          // return this.isDispElement(planningType, objectDataModel)
        } else if (PLANNING_TYPE.UNIT_FACE_OPTION === this.addCtrlSpace.planningType) {
          if (this.addTargetSpace && this.addTargetSpace.parentSpaceId === spaceId) {
            // 追加空間が表面オプションかつ、追加対象空間のパーツの親のユニット空間である場合
            return true
          } else if (this.addTargetSpace && this.addTargetSpace.id === spaceId) {
            // 追加空間が表面オプションかつ、追加対象空間のパーツである場合
            return false
          }
        } else if (this.addTargetSpace && this.addTargetSpace.id === spaceId) {
          // 追加対象セル空間の場合
          if (spaceObjectType !== OBJECT_TYPE.COMBINE.code) {
            return true
          }
        } else if (this.editTargetCellSpaceParent &&
          this.editTargetCellSpaceParent.id === parentSpaceId &&
          spaceObjectType === OBJECT_TYPE.CELL.code) {
          // セル比率設定対象空間の場合
          return true
        }
        // 空間に図面透過表示フラグ2が立っている場合
        if (objectDataModel != null && objectDataModel.objDtlTypeMaster != null &&
          objectDataModel.objDtlTypeMaster.objDtlTypeSheetDrawFlg === 2) {
          return true
        }
        // **********************************************************************
        // 扉開閉線による制御
        // **********************************************************************
        if (this.dispSpaceObjectTypes.includes(OBJECT_TYPE.PARTS.code) &&
          this.doorOpenLineMap.hasOwnProperty(spaceId)) {
          return true
        }

        if (this.isDispAccessory && planningType === PLANNING_TYPE.ACCESSORY) return true

        return false
      }).map(space => {
        SpaceUtil.rotateSpaceDataForDisp(space, viewType, rotate)
        return {
          ...space,
          isDispOnlyDoorOpenLine: this.isDispOnlyDoorOpenLine(space)
        }
      })
    },
    /** 強調空間 */
    dispFocusedSpace: function () {
      return this.dispGlobalSpaces.find(space => space.id === this.focusedSpaceId)
    },
    /** 隠れた空間マップ */
    hiddenSpacesMap: function () {
      if (this.dispGlobalSpaces === null && !Object.keys(this.dispGlobalSpaces)) return {}

      const lastDispSpaceMapKeys = Object.keys(this.lastDispSpaceMap)
      const hiddenSpacesMap = {}
      lastDispSpaceMapKeys.forEach(spaceId => {
        hiddenSpacesMap[spaceId] = this.getHiddenSpaces(spaceId)
      })
      return hiddenSpacesMap
    },
    /** {スケルトン配下の空間の中で最後に表示する空間ID：スケルトン配下の中で表示する空間の配列,,,,,} */
    lastDispSpaceMap () {
      const lastDispSpaceMap = {}
      Object.keys(this.sklUnderSpaceIdsMap).forEach(key => {
        const lastDispSpaces = []
        let lastDispSpaceId = 0
        this.dispGlobalSpaces.forEach(dispGlobalSpace => {
          if (this.sklUnderSpaceIdsMap[key].includes(dispGlobalSpace.id)) {
            lastDispSpaces.push(dispGlobalSpace)
            lastDispSpaceId = dispGlobalSpace.id
          }
        })
        lastDispSpaceMap[lastDispSpaceId] = lastDispSpaceMap[lastDispSpaceId]
          // 既にキーが存在していたら追加（セルまたぎの扉がある場合）
          ? Array.from(new Set([...lastDispSpaceMap[lastDispSpaceId], ...lastDispSpaces]))
          : lastDispSpaces
      })
      return lastDispSpaceMap
    },
    condPositionGuideLines () {
      let lines = []
      this.positionGuideLines.forEach(data => {
        let line = SpaceUtil.convertGuideLineDataForDisp(data, this.viewSet.viewType, this.sectionPosition)
        if (Object.keys(line).length) {
          lines.push(line)
        }
      })
      return lines.filter(({ x1, x2, y1, y2 }) => x1 !== x2 || y1 !== y2)
    },
    // *****************************************************************
    // 表示線制御
    // *****************************************************************
    axisLine: function () {
      return SVG_LINE.AXIS_LINE
    },
    gridLine: function () {
      return SVG_LINE.GRID_LINE
    },
    editSpacesLine: function () {
      return SVG_LINE.EDIT_SPACES
    },
    groupCtrlSpacesLine: function () {
      return SVG_LINE.GROUP_CTRL_SPACES
    },
    locateAddSpaceLine: function () {
      return SVG_LINE.LOCATE_ADD_SPACE
    },
    targetSwapSpaceLine: function () {
      return SVG_LINE.TARGET_SWAP_SPACE
    },
    targetFaceOpUnitAddSpaceLine: function () {
      return SVG_LINE.TARGET_FACEOP_ADD_SPACE
    },
    addCtrlSpaceLine: function () {
      return SVG_LINE.ADD_CTRL_SPACE
    },
    focusSpaceLine: function () {
      return SVG_LINE.FOCUS_SPACE
    },
    dispSnapLine: function () {
      return SVG_LINE.SNAP_LINES
    },
    dispSectionLine: function () {
      return SVG_LINE.SECTION_LINES
    },
    sectionPointLine: function () {
      return SVG_LINE.SECTION_POINT_LINES
    },
    faceBeforeLine: function () {
      return SVG_LINE.FACE_LINES_BEFORE
    },
    faceAfterLine: function () {
      return SVG_LINE.FACE_LINES_AFTER
    },
    selectAreaLine: function () {
      return SVG_LINE.SELECT_AREA
    },
    positionGuideLine: function () {
      return SVG_LINE.POSITION_GUIDE
    }
  },
  methods: {
    // *****************************************************************
    // イベントハンドラ
    // *****************************************************************
    onMousemove: function (event) {
      // キャンバス上の座標をセット
      this.$emit('setCurrentPointerPositionOnCanvas', event, this.viewBox)
    },
    onMouseover: function (event) {
      this.$emit('setCurrentView', this.viewSet.viewType, this.canvasType)
      this.$emit('setOnCanvas', true)
    },
    onMouseout: function (event) {
      this.$emit('setOnCanvas', false)
    },
    onWheel: function (event) {
      event.preventDefault()
      this.$emit('zoomCanvas', event.wheelDelta)
    },
    onViewTypeChanged: function (event) {
      this.$emit('setViewSet', this.selectedViewType)
    },
    // *****************************************************************
    // 表示制御(キャンバス)
    // *****************************************************************
    /** 断面線太さ */
    dispSectionPositionStrokeWidth: function (axis) {
      if ((AXIS_TYPE.X === axis && AXIS_TYPE.X === this.activeSectionPosition) ||
        (AXIS_TYPE.Y === axis && AXIS_TYPE.Y === this.activeSectionPosition)) {
        return this.scaleStrokeWidth(this.dispSectionLine.width)
      }
      return 0
    },
    dispSectionPointStrokeWidth: function (lineNo) {
      if (this.activeSectionPoint !== null && lineNo === this.activeSectionPoint) {
        return this.scaleStrokeWidth(this.sectionPointLine.width)
      }
      return 0
    },
    /** 断面線移動アイコン */
    dispSideIcon: function () {
      return 'M0 0 L ' + this.scaleStrokeWidth(25) + ' ' + this.scaleStrokeWidth(25) + ' L0 ' + this.scaleStrokeWidth(50) + ' Z'
    },
    dispBottomIcon: function () {
      return 'M0 0 L ' + this.scaleStrokeWidth(50) + ' 0 L ' + this.scaleStrokeWidth(25) + ' ' + this.scaleStrokeWidth(25) + ' Z'
    },
    /** ピッチ指定での数値リスト */
    pitchList: function (pitch, min, max) {
      const tmp = []
      if (!pitch) return tmp
      const minIdx = Math.floor(min / pitch)
      const maxIdx = Math.ceil(max / pitch)
      for (let i = minIdx; i < maxIdx; i++) {
        tmp.push(i * pitch)
      }
      return tmp
    },
    markerEndOffset: function (start, end) {
      if (start < end) return -15
      if (start > end) return 15
      return 0
    },
    guideLineLength: function (guideLine) {
      return CalcUtil.round(CalcUtil.dist(guideLine.x1, guideLine.y1, guideLine.x2, guideLine.y2))
    },
    getDispCtrlSpace: function (space, filterBySection = true) {
      if (!space || !Object.keys(space).length) return {}
      let converted = SpaceUtil.convertSpaceDataForDisp(space, this.viewSet.viewType, this.viewSet.rotate)
      // 断面線より手前の空間の場合は非表示
      if (filterBySection) {
        if (this.viewSet.viewType.isBase) {
          if (converted.z + converted.d < this.convertedSectionPosition.z) return {}
        } else {
          if (this.convertedSectionPosition.z < converted.z) return {}
        }
      }
      return converted
    },
    getDispLocateAddSpaceLines: function () {
      return SpaceUtil.getLocateAddCtrlSpace(this.addCtrlSpace, this.addTargetSpace, this.globalSpaceMap, this.viewSet)
    },
    getDispTargetSwapSpaceLines: function () {
      return SpaceUtil.getTargetSwapSpaceLines(this.addCtrlSpace, this.addTargetSpace, this.globalSpaceMap, this.viewSet)
    },
    getDispTargetFaceOpAddSpaceLines: function () {
      return SpaceUtil.getTargetFaceOpAddSpaceLines(this.addCtrlSpace, this.addTargetSpace, this.globalSpaceMap, this.viewSet)
    },
    /** 可動棚線を表示するか */
    showInfillLine: function (space) {
      if (VIEW_TYPE.FRONT !== this.viewSet.viewType || OBJECT_TYPE.PARTS.code !== space.spaceObjectType) return false
      let parent = this.globalSpaces.find(e => e.id === space.parentSpaceId)
      if (parent && parent.planningType === PLANNING_TYPE.UNIT_INFILL) {
        let alignAtt = this.fileData.attributeModelList.find(att => {
          return ATTRIBUTE_KEY.UT_FLAG_ALIGN_DIR === att.attKeycd && att.spaceId === parent.id
        })
        // インフィルかつ配列方向がY軸であったら表示
        return alignAtt != null && alignAtt.attString1 === AXIS_TYPE.Y
      }
      return false
    },
    /** 表示対象の素材空間か */
    isDispElement: function (planningType, objectDataModel) {
      // 素材のみチェック
      if (PLANNING_TYPE.ELEMENT !== planningType) return true
      // 芯材は描画対象
      if (objectDataModel.objElFaceType === 7) return true

      switch (this.viewSet.viewType) {
        case VIEW_TYPE.RIGHT:
          return objectDataModel.objElFaceType === 1
        case VIEW_TYPE.LEFT:
          return objectDataModel.objElFaceType === 2
        case VIEW_TYPE.TOP:
          return objectDataModel.objElFaceType === 3
        case VIEW_TYPE.BOTTOM:
          return objectDataModel.objElFaceType === 4
        case VIEW_TYPE.FRONT:
          return objectDataModel.objElFaceType === 5
        case VIEW_TYPE.BACK:
          return objectDataModel.objElFaceType === 6
      }
      return false
    },
    /** 扉開閉線のみ表示する空間か */
    isDispOnlyDoorOpenLine ({ id: spaceId, spaceObjectType, parentSpaceId }) {
      // **********************************************************************
      // 種別による制御
      // **********************************************************************
      if (spaceObjectType &&
        this.dispSpaceObjectTypes &&
        this.dispSpaceObjectTypes.includes(spaceObjectType)) {
        // 表示対象オブジェクト種別の場合
        return false
      } else if (this.addTargetSpace && this.addTargetSpace.id === spaceId) {
        // 追加対象セル空間の場合
        if (spaceObjectType !== OBJECT_TYPE.COMBINE.code) {
          return false
        }
      } else if (this.editTargetCellSpaceParent &&
        this.editTargetCellSpaceParent.id === parentSpaceId &&
        spaceObjectType === OBJECT_TYPE.CELL.code) {
        // セル比率設定対象空間の場合
        return false
      } else if (spaceObjectType === OBJECT_TYPE.ROOM.code) {
        // ルーム空間の場合
        return false
      }
      // **********************************************************************
      // 扉開閉線による制御
      // **********************************************************************
      const has = Object.prototype.hasOwnProperty
      if (this.dispSpaceObjectTypes.includes(OBJECT_TYPE.PARTS.code) && has.call(this.doorOpenLineMap, spaceId)) {
        // 種別による制御で対象外の場合は扉開閉線のみ表示
        return true
      }
      return false
    },
    // *****************************************************************
    // 処理
    // *****************************************************************
    /** 断面線位置より外側に存在し非表示となる空間IDの追加・削除 */
    setOutOfSectionSpaceId: function (isAdd, viewType, spaceId) {
      this.$emit('setOutOfSectionSpaceId', isAdd, viewType, spaceId)
    },
    /** 縮尺に応じた線の太さを返す */
    scaleStrokeWidth: function (strokeWidth) {
      return strokeWidth * this.svgZoomRatio / 3
    },
    getDashArray: function (dashed) {
      dashed *= this.svgZoomRatio / 3
      return dashed + ' ' + dashed + ' '
    },
    /** 空間あたりの隠れた空間リストを取得 */
    getHiddenSpaces (spaceId) {
      // スケルトン配下の中で点線表示する空間を返す
      return this.lastDispSpaceMap[spaceId].filter(s => {
        const objectDataModel = this.objectDataModelMap[s.id]
        // 図面透過表示フラグが立っていたら表示対象
        return objectDataModel != null && objectDataModel.objDtlTypeMaster != null &&
          (objectDataModel.objDtlTypeMaster.objDtlTypeSheetDrawFlg === 1 ||
          objectDataModel.objDtlTypeMaster.objDtlTypeSheetDrawFlg === 2)
      })
    }
  }
}
</script>

<style src="../../../css/planning/planning.scss" lang="scss" scoped></style>
<style lang="scss" scoped>
svg {
  text.text-mask {
    stroke: white;
    stroke-width: 0.5em;
  }
}
</style>



// WEBPACK FOOTER //
// src/main/js/components/planning/parts/Canvas.vue